import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useRadixConnect, RadixConnectWithContext } from '../radix_connect';
import { useAuth, useGameData } from '../backend';
import '../styles/StartScreen.scss';
import { FaPlay, FaRocket, FaDesktop, FaArrowLeft } from 'react-icons/fa';
import { formatTokenAmount } from '../utils/formatTokenAmount';

const getFirstWalletAddress = (walletData) => {
  const accounts = Array.isArray(walletData?.accounts) ? walletData.accounts : [];
  return accounts[0]?.address || null;
};

// AnimatedLambo component
const AnimatedLambo = () => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [movePattern, setMovePattern] = useState('right');
  const [size, setSize] = useState(1);
  const [imageType, setImageType] = useState('lambo');

  useEffect(() => {
    // Function to show the random meme at random intervals
    const showMeme = () => {
      if (!visible) {
        // Generate completely random position that ensures meme is fully visible
        const newTop = Math.floor(Math.random() * 80); // 0-80% from top
        const newLeft = Math.floor(Math.random() * 80); // 0-80% from left
        
        // Randomly choose movement pattern
        const movePatterns = [
          'right', 'left',                 // Horizontal
          'topright', 'topleft',           // Diagonal down
          'bottomright', 'bottomleft'      // Diagonal up
        ];
        const newMovePattern = movePatterns[Math.floor(Math.random() * movePatterns.length)];
        
        // Random size variation (0.7 to 1.3)
        const newSize = 0.7 + Math.random() * 0.6;
        
        // Randomly select image type - lambo or 100x
        const newImageType = Math.random() > 0.5 ? 'lambo' : 'reddick100x';
        
        setPosition({ top: newTop, left: newLeft });
        setMovePattern(newMovePattern);
        setSize(newSize);
        setImageType(newImageType);
        setVisible(true);
        
        // Hide after a longer period - 12 seconds (2x original)
        setTimeout(() => {
          setVisible(false);
        }, 12000);
      }
    };
    
    // Initial delay before first appearance
    const initialDelay = Math.floor(Math.random() * 8000) + 3000;
    const initialTimeout = setTimeout(showMeme, initialDelay);
    
    // Show meme at random intervals
    const interval = setInterval(() => {
      // Random interval between 15 and 50 seconds
      const randomInterval = Math.floor(Math.random() * 35000) + 15000;
      setTimeout(showMeme, randomInterval);
    }, 50000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };
  }, [visible]);
  
  if (!visible) return null;
  
  return (
    <div 
      className={`animated-meme ${movePattern} ${visible ? 'visible' : ''} ${imageType}`}
      style={{ 
        top: `${position.top}%`, 
        left: `${position.left}%`,
        transform: `scale(${size})`
      }}
    />
  );
};

const StartScreen = () => {
  const navigate = useNavigate();
  const { rdt, connected, accounts, selectedAccount } = useRadixConnect();
  const [newSaveName, setNewSaveName] = useState('');
  const [showNewSaveForm, setShowNewSaveForm] = useState(false);
  const [error, setError] = useState(null);
  const [showCompanyList, setShowCompanyList] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(connected);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [shouldLoadData, setShouldLoadData] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [requestedWalletAddress, setRequestedWalletAddress] = useState(null);

  // Check if device is mobile or tablet
  useEffect(() => {
    const checkDeviceType = () => {
      // Check for touch capability
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Check window width
      const isSmallScreen = window.innerWidth < 1024;
      
      // Check for mobile user agent (as a fallback)
      const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      
      setIsMobileDevice(hasTouchScreen && (isSmallScreen || mobileUserAgent));
    };
    
    // Run check on mount
    checkDeviceType();
    
    // Re-check on window resize
    window.addEventListener('resize', checkDeviceType);
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  // Check if wallet is connected directly
  useEffect(() => {
    // Also check the button state directly
    const checkButtonState = () => {
      const connectButton = document.querySelector('radix-connect-button');
      if (connectButton && connectButton.getAttribute('state') === 'connected') {
        setIsWalletConnected(true);
      }
    };
    
    // Check immediately and then periodically
    checkButtonState();
    const interval = setInterval(checkButtonState, 1000);
    
    return () => clearInterval(interval);
  }, [connected]);

  // Derive wallet state from accounts
  const sharedWalletAddress =
    selectedAccount?.address ||
    (accounts && accounts.length > 0 ? accounts[0].address : null) ||
    requestedWalletAddress;
  const wallet = {
    rdt,
    connected: connected || isWalletConnected || Boolean(sharedWalletAddress),
    address: sharedWalletAddress,
    accounts: accounts
  };

  useEffect(() => {
    setIsWalletConnected(connected || Boolean(sharedWalletAddress));
  }, [connected, sharedWalletAddress]);

  useEffect(() => {
    if (!connected && !isWalletConnected && (!accounts || accounts.length === 0)) {
      setRequestedWalletAddress(null);
      setShouldLoadData(false);
      setShowCompanyList(false);
      setShowNewSaveForm(false);
    }
  }, [connected, isWalletConnected, accounts]);

  // Set shouldLoadData when wallet is connected
  useEffect(() => {
    if (isWalletConnected && wallet.address) {
      console.log('Wallet connected with address, setting shouldLoadData to true');
      setShouldLoadData(true);
    } else {
      console.log('Wallet not fully connected, shouldLoadData remains', shouldLoadData);
    }
  }, [isWalletConnected, wallet.address, shouldLoadData]);

  // Use auth and game data hooks
  const { 
    userId, 
    isAuthenticated, 
    isLoading: authLoading,
    error: authError,
    login,
    sessionToken,
  } = useAuth(shouldLoadData ? { ...wallet, autoLogin: false } : { connected: false, address: null, autoLogin: false });
  
  const {
    gameSave,
    isLoading: gameLoading,
    createNewSave,
    hasSave,
    error: gameError
  } = useGameData(shouldLoadData ? userId : null, sessionToken);

  // State to track if loading has taken too long
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Debug wallet state
  useEffect(() => {
    console.log('Wallet connection status:', connected);
    console.log('Wallet address:', wallet.address);
    console.log('Auth loading:', authLoading);
    console.log('Game loading:', gameLoading);
    console.log('Is authenticated:', isAuthenticated);
    console.log('User ID:', userId);
    console.log('Has save:', hasSave);
    console.log('Auth error:', authError);
    console.log('Game error:', gameError);
  }, [connected, wallet.address, authLoading, gameLoading, isAuthenticated, userId, hasSave, authError, gameError]);

  // Add timeout for loading state
  useEffect(() => {
    let timer;
    console.log('Loading status check:', { authLoading, gameLoading, shouldLoadData });
    
    if ((authLoading || gameLoading) && shouldLoadData) {
      console.log('Setting loading timeout timer');
      // Set a timeout to prevent infinite loading
      timer = setTimeout(() => {
        console.log('Loading timeout reached');
        setLoadingTimeout(true);
      }, 10000); // 10 seconds timeout
    } else {
      setLoadingTimeout(false);
    }

    return () => {
      if (timer) {
        console.log('Clearing loading timeout timer');
        clearTimeout(timer);
      }
    };
  }, [authLoading, gameLoading, shouldLoadData]);

  // Log when connected state changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Connected state changed:', connected);
    }
  }, [connected]);

  // Check if we should show company list (coming back from game)
  useEffect(() => {
    const skipRedirect = localStorage.getItem('skipRedirect') === 'true';
    if (skipRedirect) {
      setShowCompanyList(true);
      localStorage.removeItem('skipRedirect');
    }
  }, []);

  // Show/hide company list when authentication state changes
  useEffect(() => {
    if (isAuthenticated && !authLoading && !gameLoading) {
      setShowCompanyList(true);
      
      // If no save and authenticated, show form to create one
      if (!hasSave) {
        setShowNewSaveForm(true);
      }
    }
  }, [isAuthenticated, authLoading, gameLoading, hasSave]);

  const handleLoadSave = (saveId) => navigate(`/game/${saveId}`);
  
  const handleCreateSave = async () => {
    // Sanitize input
    const trimmedSaveName = newSaveName.trim();
    
    // Check for empty input
    if (!trimmedSaveName) {
      setError('Please enter a name for your company');
      return;
    }
    
    // Check length
    if (trimmedSaveName.length < 2 || trimmedSaveName.length > 50) {
      setError('Company name must be between 2 and 50 characters');
      return;
    }
    
    // Check valid characters
    const namePattern = /^[a-zA-Z0-9\s\-_'.&]+$/;
    if (!namePattern.test(trimmedSaveName)) {
      setError('Company name contains invalid characters. Use only letters, numbers, spaces, and common symbols.');
      return;
    }
    
    if (userId) {
      try {
        const save = await createNewSave(trimmedSaveName);
        
        if (save) {
          setNewSaveName('');
          setShowNewSaveForm(false);
          setError(null);
          navigate(`/game/${save.save_id}`);
        } else {
          setError('Failed to create company. Please try again.');
        }
      } catch (err) {
        // Check for rate limit errors
        if (err.rateLimit) {
          setError(`Rate limit exceeded. Please wait ${err.retryAfter} seconds before trying again.`);
          
          // Clear the error after the retry period
          setTimeout(() => {
            setError(null);
          }, err.retryAfter * 1000);
        } else {
          setError(err.message || 'An error occurred creating your company');
        }
      }
    } else {
      setError('No user ID available. Please reconnect your wallet.');
    }
  };

  const requestSharedWalletAddress = async () => {
    const cachedAddress = wallet.address || getFirstWalletAddress(rdt?.walletApi?.getWalletData?.());
    if (cachedAddress) {
      setRequestedWalletAddress(cachedAddress);
      setIsWalletConnected(true);
      setShouldLoadData(true);
      return cachedAddress;
    }

    if (!rdt?.walletApi?.sendRequest) {
      throw new Error('Radix Wallet is still preparing. Please try again in a moment.');
    }

    const result = await rdt.walletApi.sendRequest();
    if (typeof result?.isErr === 'function' && result.isErr()) {
      throw new Error(result.error?.message || 'Wallet account sharing was cancelled or failed.');
    }

    const walletData = typeof result?.isOk === 'function' && result.isOk() ? result.value : result;
    const requestedAddress =
      getFirstWalletAddress(walletData) ||
      getFirstWalletAddress(rdt.walletApi.getWalletData?.());

    if (!requestedAddress) {
      throw new Error('Please share a Radix account to manage your companies.');
    }

    setIsWalletConnected(true);
    setRequestedWalletAddress(requestedAddress);
    setShouldLoadData(true);
    return requestedAddress;
  };

  const handleStartGame = async () => {
    if (isStarting) return;

    setError(null);
    setIsStarting(true);

    try {
      const walletAddress = await requestSharedWalletAddress();

      if (!isAuthenticated) {
        const authenticatedUser = await login(walletAddress, rdt);
        if (!authenticatedUser) {
          setError('Please approve wallet ownership verification to start playing.');
          return;
        }
      }

      setShowCompanyList(true);

      if (isAuthenticated && !hasSave && !gameLoading) {
        setShowNewSaveForm(true);
      }
    } catch (err) {
      setError(err.message || 'Unable to start the game. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  // Render mobile view if on a mobile device
  if (isMobileDevice) {
    return (
      <div className="start-screen">
        <div className="start-screen-overlay"></div>
        <div className="mobile-warning-content">
          <div className="animated-bar-top"></div>
          <div className="animated-bar-bottom"></div>
          
          <div className="logo-container">
            <h1 className="game-title">
              <span className="brand-name">REDDICKS</span>
              <span className="sub-brand">GAMES</span>
            </h1>
            <h2 className="game-subtitle">CYBERPUNK PLAY-TO-EARN MEMECOIN</h2>
          </div>
          
          <div className="device-warning">
            <div className="warning-icon">
              <FaDesktop />
            </div>
            <h3>Desktop Only Game</h3>
            <p>REDDICKS Games is currently optimized for desktop computers with mouse and keyboard controls.</p>
            <p>For the best gameplay experience, please access the game from a desktop or laptop computer.</p>
            <p className="coming-soon">Mobile version coming soon!</p>
            
            <Link to="/" className="back-to-website-btn">
              <FaArrowLeft /> Back to Website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // If wallet data has not been requested yet, keep the page lightweight but
  // still expose the explicit Start action after the Radix button is used.
  if (!shouldLoadData) {
    const hasSharedAccount = Boolean(wallet.address);

    return (
      <div className="start-screen">
        <div className="start-screen-overlay"></div>
        <AnimatedLambo />
        <div className="start-screen-content">
          <div className="animated-bar-top"></div>
          <div className="animated-bar-bottom"></div>
          
          <Link to="/" className="back-to-website-link">
            <FaArrowLeft /> Back to website
          </Link>
          
          <div className="logo-container">
            <h1 className="game-title">
              <span className="brand-name">REDDICKS</span>
              <span className="sub-brand">GAMES</span>
            </h1>
            <h2 className="game-subtitle">CYBERPUNK PLAY-TO-EARN MEMECOIN</h2>
          </div>
          
          <div className="main-actions">
            <div className="connect-section">
              <h3 className="section-title">
                {hasSharedAccount ? 'WALLET CONNECTED' : 'CONNECT WALLET'}
              </h3>
              <div className="connect-button-container">
                <RadixConnectWithContext />
              </div>
              {hasSharedAccount && (
                <div className="wallet-info">
                  <div className="wallet-address">
                    <span>Connected:</span> {wallet.address}
                  </div>
                </div>
              )}
              <p className="start-info">
                {hasSharedAccount ? 'Start the game to manage your company' : 'Connect your wallet to start playing'}
              </p>
            </div>

            <div className="start-section">
              <h3 className="section-title">START GAME</h3>
              <button
                type="button"
                className={`start-game-btn ${(!rdt || isStarting) ? 'disabled' : ''}`}
                onClick={handleStartGame}
                disabled={!rdt || isStarting}
              >
                <FaRocket className="btn-icon" />
                {isStarting ? 'Starting...' : 'Start Game'}
              </button>
              <p className="start-info">
                {!rdt
                  ? 'Preparing Radix Wallet connection'
                  : hasSharedAccount
                    ? 'Continue to your company page'
                    : 'Connect your wallet, then start the game'}
              </p>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          
          <div className="footer">
            <div className="game-info">
              <p>Build your tech empire in a cutthroat cyberpunk future.</p>
              <p>Connect your Radix wallet to start your journey.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state (only show after wallet is connected)
  if ((authLoading || gameLoading) && !loadingTimeout) {
    return (
      <div className="start-screen">
        <div className="start-screen-overlay"></div>
        <div className="start-screen-content">
          <div className="animated-bar-top"></div>
          <div className="animated-bar-bottom"></div>
          
          <div className="logo-container">
            <h1 className="game-title">
              <span className="brand-name">REDDICKS</span>
              <span className="sub-brand">GAMES</span>
            </h1>
            <h2 className="game-subtitle">CYBERPUNK PLAY-TO-EARN MEMECOIN</h2>
          </div>
          
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Loading your company data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error or loading timeout message
  if (loadingTimeout || authError || gameError) {
    return (
      <div className="start-screen">
        <div className="start-screen-overlay"></div>
        <div className="start-screen-content">
          <div className="animated-bar-top"></div>
          <div className="animated-bar-bottom"></div>
          
          <div className="logo-container">
            <h1 className="game-title">
              <span className="brand-name">REDDICKS</span>
              <span className="sub-brand">GAMES</span>
            </h1>
            <h2 className="game-subtitle">CYBERPUNK PLAY-TO-EARN MEMECOIN</h2>
          </div>
          
          <div className="error-section">
            <h3>Connection Error</h3>
            <p>{authError || gameError || "Connection timed out. Please try again."}</p>
            <button 
              className="retry-button" 
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
            <div className="connect-button-container">
              <RadixConnectWithContext />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="start-screen">
      <div className="start-screen-overlay"></div>
      <AnimatedLambo />
      <div className="start-screen-content">
        <div className="animated-bar-top"></div>
        <div className="animated-bar-bottom"></div>
        
        <Link to="/" className="back-to-website-link">
          <FaArrowLeft /> Back to website
        </Link>
        
        <div className={`main-actions ${showCompanyList ? 'companies-view' : ''}`}>
          <div className={`connect-section ${showCompanyList ? 'centered' : ''}`}>
            <h3 className="section-title">
              {showCompanyList ? 'MANAGE WALLET' : wallet.address ? 'WALLET CONNECTED' : 'CONNECT WALLET'}
            </h3>
            <div className="connect-button-container">
              <RadixConnectWithContext />
            </div>
            
            {isWalletConnected && wallet.address && (
              <div className="wallet-info">
                <div className="wallet-address">
                  <span>Connected:</span> {wallet.address}
                </div>
              </div>
            )}
          </div>
          
          {!showCompanyList && (
            <div className="start-section">
              <h3 className="section-title">START GAME</h3>
              <button 
                type="button"
                className={`start-game-btn ${(!rdt || isStarting) ? 'disabled' : ''}`}
                onClick={handleStartGame}
                disabled={!rdt || isStarting}
              >
                <FaRocket className="btn-icon" />
                {isStarting ? 'Starting...' : 'Start Game'}
              </button>
              <p className="start-info">
                {!rdt
                  ? 'Preparing Radix Wallet connection'
                  : !wallet.address
                    ? 'Connect or share an account to start playing'
                    : hasSave
                      ? 'Continue with your company'
                      : 'Create your company'}
              </p>
            </div>
          )}
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {isWalletConnected && wallet.address && !hasSave && (showNewSaveForm || showCompanyList) && (
          <div className="saves-section">
            <h3 className="section-title">CREATE YOUR COMPANY</h3>
            <div className="saves-list">
              <div className="new-save-form">
                <h4>Start Your Company</h4>
                {error && <div className="error-message">{error}</div>}
                <input
                  type="text"
                  placeholder="Enter company name"
                  value={newSaveName}
                  onChange={(e) => setNewSaveName(e.target.value)}
                  className="company-name-input"
                  autoFocus
                />
                <div className="form-actions">
                  <button 
                    className="create-btn full-width"
                    onClick={handleCreateSave}
                  >
                    Create Company & Start Game
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {isWalletConnected && wallet.address && showCompanyList && hasSave && gameSave && (
          <div className="saves-section">
            <h3 className="section-title" style={{ marginBottom: '35px' }}>YOUR COMPANY</h3>
            <div className="saves-list">
              <div 
                key={gameSave.id} 
                className="save-item"
                onClick={() => handleLoadSave(gameSave.save_id)}
              >
                <div className="save-info">
                  <div className="save-name">{gameSave.company_name}</div>
                  <div className="save-details">
                    <span>Created: {new Date(gameSave.created_at).toLocaleDateString()}</span>
                    <span>Last played: {new Date(gameSave.last_played).toLocaleDateString()}</span>
                    <span>Balance: {formatTokenAmount(gameSave.money)} DCKSLAP</span>
                  </div>
                </div>
                <div className="save-actions">
                  <button className="play-btn" title="Play">
                    <FaPlay className="btn-icon" /> PLAY
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="footer">
          <div className="game-info">
            <p>Build your tech empire in a cutthroat cyberpunk future.</p>
            {!showCompanyList && (
              <p>Connect your Radix wallet to start your journey.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartScreen;
